import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Location, NgClass, NgTemplateOutlet } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { BaseFormComponent } from 'components/form/base-form';
import { ApiService } from 'services/api.service';
import { ToastService } from 'services/toast.service';
import { IrFormLabelsService } from 'services/ir-form-labels.service';
import { ClientCard } from 'types/client.interface';
import { LegalEntityCard, LegalEntityFormOption, UpdateLegalEntityRequest } from 'types/legal-entity.interface';
import { CountryDto } from 'types/country.interface';
import { StructuralUnitMinimalListItem } from 'types/structural-unit.interface';
import { forkJoin, Observable } from 'rxjs';
import {
  InformationRequestCard,
  UpdateBoInformationRequestRequest,
  InformationRequestTopicResponse,
  RequesterRelationType,
  RequesterForOtherPersonType,
  PayerType,
  ResponseDeliveryMethod,
  InvoiceDeliveryMethod,
  ServiceDeadlineType,
  DiscountGroupType,
  InformationRequestPurpose,
  FILE_OBJECT_TYPE_INFO_REQUEST_AUTHORIZATION,
  FILE_OBJECT_TYPE_INFO_REQUEST_DISCOUNT,
  FILE_OBJECT_TYPE_INFO_REQUEST_DEATH_CERTIFICATE,
  CLIENT_PAYER_FILE_TYPE_BY_KEY
} from 'types/information-request.interface';
import { ServiceFormActionsComponent } from 'components/service/service-form-actions/service-form-actions.component';
import { ConfirmModalComponent } from 'components/modal/confirm-modal/confirm-modal.component';
import { TooltipComponent } from 'components/tooltip/tooltip.component';
import { FileDescription, FileUploadComponent } from 'components/file-upload/file-upload.component';
import { FileCard } from 'types/file.interface';
import { DatePickerComponent, DateRangeMode } from 'components/date-picker/date-picker.component';
import {
  MAX_LENGTH_NAME,
  MAX_LENGTH_PHONE,
  MIN_LENGTH_PHONE,
  MAX_LENGTH_EMAIL,
  phoneValidator,
  emailValidator
} from 'util/form.utility';

const MAX_TEXT_LENGTH = 100;
const MAX_LONG_TEXT_LENGTH = 255;
const MAX_NOTES_LENGTH = 500;
const MAX_PK_LENGTH = 12;
const MAX_LETTER_NUMBER_LENGTH = 50;
const MAX_PERSONAL_CODE_LENGTH = 50;
const MAX_ABOUT_SUBJECT_LENGTH = 50;

@Component({
  selector: 'app-info-request-client-payer-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    NgClass,
    NgTemplateOutlet,
    ServiceFormActionsComponent,
    ConfirmModalComponent,
    TooltipComponent,
    FileUploadComponent,
    DatePickerComponent
  ],
  templateUrl: './info-request-client-payer-form.component.html',
  styleUrls: ['../../../form/base-form.scss', './info-request-client-payer-form.component.scss'],
  host: {
    '[class.inline-edit]': 'inlineEditMode'
  }
})
export class InfoRequestClientPayerFormComponent extends BaseFormComponent implements OnInit, OnDestroy {
  readonly fileTypeAuthorization = FILE_OBJECT_TYPE_INFO_REQUEST_AUTHORIZATION;
  readonly fileTypeDiscount = FILE_OBJECT_TYPE_INFO_REQUEST_DISCOUNT;
  readonly fileTypeDeathCertificate = FILE_OBJECT_TYPE_INFO_REQUEST_DEATH_CERTIFICATE;

  get existingAuthorizationFiles(): FileCard[] {
    if (this.isOrganization) {
      if (!this.hasMeaningfulAuthorizationData(this.application!)) return [];
    } else {
      const savedType = this.application?.requesterForOtherPersonType;
      if (savedType && savedType !== 'AuthorizationOrPermission') return [];
    }
    return (this.application?.documents || []).filter(d => d.objectType === this.fileTypeAuthorization);
  }

  get existingDiscountFiles(): FileCard[] {
    if (this.discountTypeWasChanged) return [];
    return (this.application?.documents || []).filter(d => d.objectType === this.fileTypeDiscount);
  }

  get existingDeathCertificateFiles(): FileCard[] {
    const savedType = this.application?.requesterForOtherPersonType;
    if (savedType && savedType !== 'DeceasedRelative') return [];
    return (this.application?.documents || []).filter(d => (d.objectType as string) === this.fileTypeDeathCertificate);
  }

  @Input() client?: ClientCard;
  @Input() application?: InformationRequestCard;
  @Input() selectedTopic?: InformationRequestTopicResponse;
  @Input() isSubmittingFiles: boolean = false;
  @Output() applicationUpdated = new EventEmitter<InformationRequestCard>();
  @Output() formChanged = new EventEmitter<boolean>();
  @Output() draftSaved = new EventEmitter<void>();
  @Output() fileUploading = new EventEmitter<Observable<any>[]>();
  @Input() savedClientPayerFiles: Record<string, { files: File[]; descriptions: FileDescription[] }> = {};
  @Output() clientPayerFilesChanged = new EventEmitter<Record<string, { files: File[]; descriptions: FileDescription[] }>>();
  @Output() legalEntityUpdated = new EventEmitter<LegalEntityCard>();
  @Output() jpCompanyDataChanged = new EventEmitter<Record<string, string>>();
  @Input() fpContactData: { email?: string; phone?: string } = {};
  @Output() fpContactDataChanged = new EventEmitter<{ email?: string; phone?: string }>();
  @Input() inlineEditMode: boolean = false;
  @Output() inlineEditSaved = new EventEmitter<InformationRequestCard>();

  @ViewChild('jpAuthorizationUpload') jpAuthorizationUpload?: FileUploadComponent;
  @ViewChild('authorizationUpload') authorizationUpload?: FileUploadComponent;
  @ViewChild('deathCertificateUpload') deathCertificateUpload?: FileUploadComponent;
  @ViewChild('discountGroupUpload') discountGroupUpload?: FileUploadComponent;

  private clientPayerFiles: Record<string, { files: File[]; descriptions: FileDescription[] }> = {};
  private pendingServerFileDeletions: string[] = [];
  private clearedFileTypes: Set<string> = new Set();
  /** Set by markFilesChanged(); a file add/remove leaves the reactive form pristine. */
  private filesTouched: boolean = false;

  // Shared with the summary, which sends the queued step-2 files on "Iesniegt" (LNA-1850).
  private readonly fileTypeByKey = CLIENT_PAYER_FILE_TYPE_BY_KEY;

  /**
   * File types the operator has staged uploads for in this save. Turning a section ON also
   * fires clearFileUpload (the reveal changes the discriminator control), which marks the type
   * as "cleared" — without this guard the post-save strip would delete the file that was just
   * uploaded to that very type (LNA-1973).
   */
  private typesBeingUploaded(): Set<string> {
    return new Set(
      [this.jpAuthorizationUpload, this.authorizationUpload, this.deathCertificateUpload, this.discountGroupUpload]
        .filter((u): u is FileUploadComponent => !!u && u.files.length > 0)
        .map(u => u.type as string)
    );
  }

  private clearedTypesExcludingUploads(): Set<string> {
    const uploading = this.typesBeingUploaded();
    return new Set([...this.clearedFileTypes].filter(t => !uploading.has(t)));
  }

  private clearFileUpload(upload: FileUploadComponent | undefined, key: string): void {
    if (this.isPopulating) return;
    if (upload) {
      upload.clear();
    }

    // Track cleared file type and schedule server file deletion
    const fileType = this.fileTypeByKey[key];
    if (fileType) {
      this.clearedFileTypes.add(fileType);

      if (this.application?.documents) {
        this.application.documents
          .filter(d => (d.objectType as string) === fileType)
          .forEach(f => {
            if (f.outerId && !this.pendingServerFileDeletions.includes(f.outerId)) {
              this.pendingServerFileDeletions.push(f.outerId);
            }
          });
        this.application = {
          ...this.application,
          documents: this.application.documents.filter(d => (d.objectType as string) !== fileType)
        };
        this.applicationUpdated.emit(this.application);
      }
    }

    delete this.clientPayerFiles[key];
    delete this.savedClientPayerFiles[key];
    this.clientPayerFilesChanged.emit({ ...this.clientPayerFiles });
  }

  triggerFileUploads(): void {
    this.jpAuthorizationUpload?.handleFileRequests();
    this.authorizationUpload?.handleFileRequests();
    this.deathCertificateUpload?.handleFileRequests();
    this.discountGroupUpload?.handleFileRequests();
  }

  private collectFileObservables(): Observable<any>[] {
    const observables: Observable<any>[] = [];
    const uploads = [this.jpAuthorizationUpload, this.authorizationUpload, this.deathCertificateUpload, this.discountGroupUpload];
    for (const upload of uploads) {
      if (!upload) continue;
      const sub = upload.uploading.subscribe((obs: Observable<any>[]) => observables.push(...obs));
      upload.handleFileRequests();
      sub.unsubscribe();
    }
    return observables;
  }

  onClientPayerFileChanged(key: string, data: { files: File[]; descriptions: FileDescription[] }): void {
    this.clientPayerFiles[key] = data;
    this.clientPayerFilesChanged.emit({ ...this.clientPayerFiles });
  }

  /** A file operation never touches the reactive form, so the wizard has to be told separately. */
  markFilesChanged(): void {
    this.filesTouched = true;
    this.formChanged.emit(true);
  }

  public readonly labels = inject(IrFormLabelsService);
  public readonly maxFieldLength = MAX_LENGTH_NAME;
  public legalEntityFormOptions: LegalEntityFormOption[] = [];
  public countries: CountryDto[] = [];
  public readonly dateRangeMode = DateRangeMode;
  isLoading = false;
  validationTriggered = false;
  private isPopulating = false;
  private initialDiscountGroupType: string | null = null;
  private discountTypeWasChanged = false;
  legalEntity?: LegalEntityCard;
  structuralUnits: StructuralUnitMinimalListItem[] = [];

  constructor(
    protected override location: Location,
    protected override router: Router,
    protected override formBuilder: FormBuilder,
    protected override apiService: ApiService,
    protected override toastService: ToastService,
    protected override translateService: TranslateService
  ) {
    super(location, router, formBuilder, apiService, toastService, translateService);
  }

  override ngOnInit(): void {
    this.labels.ensureLoaded();
    this.isPopulating = true;
    this.clientPayerFiles = { ...this.savedClientPayerFiles };
    this.fillForm();
    this.apiService.getStructuralUnitOptions().subscribe({
      next: (data) => { this.structuralUnits = data; },
      error: (err) => console.error(err)
    });
    this.apiService.getLegalEntityForms().subscribe({
      next: (options) => { this.legalEntityFormOptions = options; },
      error: (err) => console.error(err)
    });
    this.apiService.getCountries().subscribe({
      next: (data) => {
        this.countries = (data.list ?? []).sort((a, b) => {
          if (a.code === 'LV') return -1;
          if (b.code === 'LV') return 1;
          return (a.name ?? '').localeCompare(b.name ?? '', 'lv');
        });
      },
      error: (err) => console.error(err)
    });
    this.populateFromApplication();
    this.applyDefaults();
    if (this.inlineEditMode) {
      this.form.get('submissionChannel')?.clearValidators();
      this.form.get('submissionChannel')?.updateValueAndValidity();
    }
    this.isPopulating = false;
    const legalEntityId = this.client?.legalEntityId || this.application?.legalEntityId;
    if (this.isOrganization && legalEntityId) {
      this.apiService.getLegalEntity(legalEntityId).subscribe({
        next: (data) => {
          this.legalEntity = data;
          this.legalEntityUpdated.emit(data);
          this.prefillLegalForm(data);
        },
        error: (err) => console.error(err)
      });
    }
    this.form.markAsPristine();
    this.formChanged.emit(false);
    this.form.valueChanges.subscribe(() => {
      this.formChanged.emit(this.form.dirty);
    });
    Promise.resolve().then(() => {
      this.form.markAsPristine();
      this.formChanged.emit(false);
    });
  }

  ngOnDestroy(): void {
    if (!this.isOrganization) {
      this.fpContactDataChanged.emit({
        email: this.form.get('email')?.value || undefined,
        phone: this.form.get('phone')?.value || undefined
      });
    }
  }

  override shouldRenderError(key: string, formGroup?: FormGroup<any>): boolean {
    if (this.validationTriggered) {
      const control = !formGroup ? this.form.get(key) : formGroup.get(key);
      if (control?.invalid) return true;
    }
    return super.shouldRenderError(key, formGroup);
  }

  get isOrganization(): boolean {
    return this.client?.clientType === 'Organization' || !!this.application?.legalEntity;
  }

  get isPersonalCodeEditable(): boolean {
    return this.application?.personalCodeEditable ?? false;
  }

  // Portal-submitted JP requests carry the amatpersona's latvija.lv contact data — read-only
  // (LNA-1977). A missing field (older BE) defaults to editable.
  get isContactPersonEditable(): boolean {
    return this.application?.contactPersonEditable !== false;
  }

  get personalCodeDisplayValue(): string {
    return this.application?.personalCode || this.client?.officialDocumentId || this.client?.nationalDigitalId || '-';
  }

  override fillForm(): void {
    this.form = this.formBuilder.group({
      personalCode: [this.application?.personalCode || this.client?.officialDocumentId || ''],
      phone: [this.client?.phone || '', [phoneValidator(), Validators.minLength(MIN_LENGTH_PHONE), Validators.maxLength(MAX_LENGTH_PHONE)]],
      email: ['', [emailValidator(), Validators.maxLength(MAX_LENGTH_EMAIL)]],

      // JP company editable fields
      legalAddress: [''],
      legalForm: [''],
      companyPhone: [''],
      companyEmail: [''],

      // JP contact person
      contactPersonName: ['', Validators.maxLength(MAX_TEXT_LENGTH)],
      contactPersonSurname: ['', Validators.maxLength(MAX_TEXT_LENGTH)],
      contactPersonEmail: ['', [emailValidator(), Validators.maxLength(MAX_LENGTH_EMAIL)]],
      contactPersonPhone: [''],

      // JP authorization checkbox
      jpAuthorizationChecked: [false],

      // Informaciju pieprasu (FP only)
      requesterRelationType: [null as RequesterRelationType | null],
      requesterForOtherPersonType: [null as RequesterForOtherPersonType | null],

      // Requester other person data
      otherPersonName: [''],
      otherPersonSurname: [''],
      otherPersonCode: [''],
      authorizationNumber: [''],
      relationshipType: [''],
      deathCertificateData: [''],
      otherPersonDescription: [''],

      // Discount group (FP only)
      discountGroupType: [null as DiscountGroupType | 'None' | null],
      discountName: [''],
      discountSurname: [''],
      discountDocumentNumber: [''],
      discountDocumentExpiry: [''],
      discountDocumentIssuer: [''],
      discountReason: [''],

      // Payment
      payerType: [null as PayerType | null, Validators.required],
      payerName: [''],
      payerSurname: [''],
      payerPersonalCode: [''],
      payerCompanyName: [''],
      payerRegistrationNumber: [''],
      payerCountry: [''],
      payerVatNumber: [''],
      payerLegalEntityName: [''],
      payerLegalForm: [''],
      payerFreeOfChargeReason: ['', Validators.maxLength(MAX_LONG_TEXT_LENGTH)],

      // Invoice delivery
      invoiceDeliveryMethod: [null as InvoiceDeliveryMethod | null, Validators.required],
      invoiceEmail: [''],
      invoiceEAddress: [''],
      invoiceAddress: [''],
      invoiceDeliveryStructuralUnitId: [''],

      // Purpose
      purpose: [null as InformationRequestPurpose | null, Validators.required],
      purposeInstitutionName: [''],
      vsaaFlag: [false],

      // Service deadline
      serviceDeadline: ['Days30' as ServiceDeadlineType | null, Validators.required],
      serviceDeadlineDate: [''],

      // Response delivery
      responseDeliveryMethod: [null as ResponseDeliveryMethod | null, Validators.required],
      responseEmail: [''],
      responseAddress: [''],
      responseEAddress: [''],
      responseDeliveryStructuralUnitId: [''],

      // Notes
      notes: ['', Validators.maxLength(MAX_NOTES_LENGTH)],

      // BO-specific fields
      submissionChannel: ['', Validators.required],
      letterNumber: ['', Validators.maxLength(MAX_LETTER_NUMBER_LENGTH)],
      aboutSubject: ['', Validators.maxLength(MAX_ABOUT_SUBJECT_LENGTH)],
      requiresRestrictedAccess: [false]
    });

    this.setupConditionalValidation();
    this.setupClientTypeValidation();
  }

  private setupClientTypeValidation(): void {
    if (this.isOrganization) {
      // JP: company fields
      this.form.get('legalAddress')?.setValidators([Validators.maxLength(MAX_LONG_TEXT_LENGTH)]);
      this.form.get('legalForm')?.setValidators([Validators.required, Validators.maxLength(MAX_LENGTH_NAME)]);
      this.form.get('companyPhone')?.setValidators([phoneValidator(), Validators.minLength(MIN_LENGTH_PHONE), Validators.maxLength(MAX_LENGTH_PHONE)]);
      this.form.get('companyEmail')?.setValidators([emailValidator(), Validators.maxLength(MAX_LENGTH_EMAIL)]);
      this.form.get('contactPersonPhone')?.setValidators([phoneValidator(), Validators.minLength(MIN_LENGTH_PHONE), Validators.maxLength(MAX_LENGTH_PHONE)]);
      this.form.get('contactPersonName')?.setValidators([Validators.required, Validators.maxLength(MAX_TEXT_LENGTH)]);
      this.form.get('contactPersonSurname')?.setValidators([Validators.required, Validators.maxLength(MAX_TEXT_LENGTH)]);
      this.form.get('contactPersonName')?.updateValueAndValidity();
      this.form.get('contactPersonSurname')?.updateValueAndValidity();

      // JP: no requesterRelationType required, no purpose required, no discount (until auth checked), no FP phone
      this.form.get('requesterRelationType')?.clearValidators();
      this.form.get('purpose')?.clearValidators();
      this.form.get('discountGroupType')?.clearValidators();
      this.form.get('discountGroupType')?.updateValueAndValidity();
      this.form.get('phone')?.clearValidators();
      this.form.get('phone')?.updateValueAndValidity();

      // JP authorization checkbox
      this.form.get('jpAuthorizationChecked')?.valueChanges.subscribe((checked: boolean) => {
        this.clearRequesterOtherPersonFields();
        this.clearFileUpload(this.jpAuthorizationUpload, 'jpAuthorization');
        if (checked) {
          this.form.get('otherPersonName')?.setValidators([Validators.maxLength(MAX_TEXT_LENGTH)]);
          this.form.get('otherPersonSurname')?.setValidators([Validators.maxLength(MAX_TEXT_LENGTH)]);
          this.form.get('otherPersonCode')?.setValidators([Validators.maxLength(MAX_TEXT_LENGTH)]);
          this.form.get('authorizationNumber')?.setValidators([Validators.maxLength(MAX_LONG_TEXT_LENGTH)]);
        } else {
          this.form.get('discountGroupType')?.clearValidators();
          this.form.get('discountGroupType')?.reset(null);
          this.clearDiscountFields();
          this.clearFileUpload(this.discountGroupUpload, 'discountGroup');
        }
        this.form.get('discountGroupType')?.updateValueAndValidity();
        this.updateOtherPersonValidity();
      });
    } else {
      // FP: requesterRelationType required
      this.form.get('requesterRelationType')?.setValidators(Validators.required);
    }
    this.form.get('requesterRelationType')?.updateValueAndValidity();
    this.form.get('purpose')?.updateValueAndValidity();
    this.setupPersonalCodeValidation();
  }

  private setupPersonalCodeValidation(): void {
    if (this.isPersonalCodeEditable) {
      this.form.get('personalCode')?.setValidators([Validators.maxLength(MAX_PERSONAL_CODE_LENGTH)]);
    } else {
      this.form.get('personalCode')?.clearValidators();
    }
    this.form.get('personalCode')?.updateValueAndValidity();
  }

  private setupConditionalValidation(): void {
    this.form.get('requesterRelationType')?.valueChanges.subscribe((val: RequesterRelationType) => {
      this.submitAttempted = false;
      this.clearRequesterOtherPersonFields();
      this.clearFileUpload(this.authorizationUpload, 'authorization');
      this.clearFileUpload(this.deathCertificateUpload, 'deathCertificate');
      if (val === 'ForOtherPerson') {
        this.form.get('requesterForOtherPersonType')?.setValidators(Validators.required);
      } else if (val === 'OtherCase') {
        this.form.get('otherPersonDescription')?.setValidators([Validators.maxLength(MAX_LONG_TEXT_LENGTH)]);
        this.form.get('requesterForOtherPersonType')?.clearValidators();
        this.form.get('requesterForOtherPersonType')?.reset(null);
      } else {
        this.form.get('requesterForOtherPersonType')?.clearValidators();
        this.form.get('requesterForOtherPersonType')?.reset(null);
      }
      this.form.get('requesterForOtherPersonType')?.updateValueAndValidity();
      this.updateOtherPersonValidity();
    });

    this.form.get('requesterForOtherPersonType')?.valueChanges.subscribe((val: RequesterForOtherPersonType) => {
      this.submitAttempted = false;
      this.clearRequesterOtherPersonFields();
      this.clearFileUpload(this.authorizationUpload, 'authorization');
      this.clearFileUpload(this.deathCertificateUpload, 'deathCertificate');
      if (val === 'AuthorizationOrPermission') {
        this.form.get('otherPersonName')?.setValidators([Validators.maxLength(MAX_TEXT_LENGTH)]);
        this.form.get('otherPersonSurname')?.setValidators([Validators.maxLength(MAX_TEXT_LENGTH)]);
        this.form.get('otherPersonCode')?.setValidators([Validators.maxLength(MAX_TEXT_LENGTH)]);
        this.form.get('authorizationNumber')?.setValidators([Validators.maxLength(MAX_LONG_TEXT_LENGTH)]);
      } else if (val === 'DeceasedRelative') {
        this.form.get('relationshipType')?.setValidators([Validators.maxLength(MAX_TEXT_LENGTH)]);
        this.form.get('otherPersonName')?.setValidators([Validators.maxLength(MAX_TEXT_LENGTH)]);
        this.form.get('otherPersonSurname')?.setValidators([Validators.maxLength(MAX_TEXT_LENGTH)]);
        this.form.get('otherPersonCode')?.setValidators([Validators.maxLength(MAX_TEXT_LENGTH)]);
      } else if (val === 'Other') {
        this.form.get('otherPersonDescription')?.setValidators([Validators.maxLength(MAX_LONG_TEXT_LENGTH)]);
      }
      this.updateOtherPersonValidity();
    });

    this.form.get('discountGroupType')?.valueChanges.subscribe(() => {
      this.submitAttempted = false;
      this.clearDiscountFields();
      this.clearFileUpload(this.discountGroupUpload, 'discountGroup');
      if (!this.isPopulating && this.initialDiscountGroupType) {
        this.discountTypeWasChanged = true;
      }
      const val = this.form.get('discountGroupType')?.value;
      if (val === 'None') {
        // No discount - no additional fields
      } else if (val === 'ChildProtection') {
        this.form.get('discountReason')?.setValidators([Validators.maxLength(MAX_LONG_TEXT_LENGTH)]);
      } else if (val === 'OtherCase') {
        this.form.get('discountReason')?.setValidators([Validators.required, Validators.maxLength(MAX_LONG_TEXT_LENGTH)]);
      } else if (val === 'PoliticallyRepressed') {
        this.form.get('discountName')?.setValidators([Validators.maxLength(MAX_TEXT_LENGTH)]);
        this.form.get('discountSurname')?.setValidators([Validators.maxLength(MAX_TEXT_LENGTH)]);
        this.form.get('discountDocumentNumber')?.setValidators([Validators.maxLength(MAX_TEXT_LENGTH)]);
      } else if (val === 'FirstOrSecondGroupDisability') {
        this.form.get('discountName')?.setValidators([Validators.maxLength(MAX_TEXT_LENGTH)]);
        this.form.get('discountSurname')?.setValidators([Validators.maxLength(MAX_TEXT_LENGTH)]);
        this.form.get('discountDocumentNumber')?.setValidators([Validators.maxLength(MAX_TEXT_LENGTH)]);
        this.form.get('discountDocumentExpiry')?.setValidators([]);
      } else if (val === 'LowIncomePerson') {
        this.form.get('discountName')?.setValidators([Validators.maxLength(MAX_TEXT_LENGTH)]);
        this.form.get('discountSurname')?.setValidators([Validators.maxLength(MAX_TEXT_LENGTH)]);
        this.form.get('discountDocumentNumber')?.setValidators([Validators.maxLength(MAX_TEXT_LENGTH)]);
        this.form.get('discountDocumentExpiry')?.setValidators([]);
        this.form.get('discountDocumentIssuer')?.setValidators([Validators.maxLength(MAX_TEXT_LENGTH)]);
      }
      this.updateDiscountValidity();
    });

    this.form.get('payerType')?.valueChanges.subscribe((val: PayerType) => {
      this.submitAttempted = false;
      this.clearPayerFields();
      if (val === 'OtherPhysicalPerson') {
        this.form.get('payerName')?.setValidators([Validators.maxLength(MAX_TEXT_LENGTH)]);
        this.form.get('payerSurname')?.setValidators([Validators.maxLength(MAX_TEXT_LENGTH)]);
        this.form.get('payerPersonalCode')?.setValidators([Validators.maxLength(MAX_PK_LENGTH)]);
      } else if (val === 'OtherLegalEntity') {
        this.form.get('payerCountry')?.clearValidators();
        this.form.get('payerRegistrationNumber')?.setValidators([Validators.maxLength(MAX_LETTER_NUMBER_LENGTH)]);
        this.form.get('payerLegalEntityName')?.setValidators([Validators.maxLength(MAX_LONG_TEXT_LENGTH)]);
        this.form.get('payerLegalForm')?.clearValidators();
        this.form.get('payerVatNumber')?.setValidators([Validators.maxLength(MAX_LETTER_NUMBER_LENGTH)]);
      } else if (val === 'FreeOfCharge') {
        this.form.get('payerFreeOfChargeReason')?.setValidators([Validators.maxLength(MAX_LONG_TEXT_LENGTH)]);
      }
      this.updatePayerValidity();
      this.syncInvoiceDeliveryWithPayerType(val);
    });

    this.form.get('invoiceDeliveryMethod')?.valueChanges.subscribe((val: InvoiceDeliveryMethod) => {
      this.form.get('invoiceEmail')?.clearValidators();
      this.form.get('invoiceEAddress')?.clearValidators();
      this.form.get('invoiceAddress')?.clearValidators();
      if (val === 'Email') {
        this.form.get('invoiceEmail')?.setValidators([emailValidator(), Validators.maxLength(MAX_LENGTH_EMAIL)]);
        this.form.get('invoiceEAddress')?.reset('');
        this.form.get('invoiceAddress')?.reset('');
      } else if (val === 'EAddress') {
        this.form.get('invoiceEAddress')?.setValidators([Validators.maxLength(MAX_LETTER_NUMBER_LENGTH)]);
        this.form.get('invoiceEmail')?.reset('');
        this.form.get('invoiceAddress')?.reset('');
      } else if (val === 'PostalMail') {
        this.form.get('invoiceAddress')?.setValidators([Validators.maxLength(MAX_LONG_TEXT_LENGTH)]);
        this.form.get('invoiceEmail')?.reset('');
        this.form.get('invoiceEAddress')?.reset('');
      } else {
        this.form.get('invoiceEmail')?.reset('');
        this.form.get('invoiceEAddress')?.reset('');
        this.form.get('invoiceAddress')?.reset('');
      }
      const invoiceUnitControl = this.form.get('invoiceDeliveryStructuralUnitId');
      if (val === 'Archive') {
        invoiceUnitControl?.setValidators(Validators.required);
      } else {
        invoiceUnitControl?.clearValidators();
        invoiceUnitControl?.reset('');
      }
      invoiceUnitControl?.updateValueAndValidity();
      this.form.get('invoiceEmail')?.updateValueAndValidity();
      this.form.get('invoiceEAddress')?.updateValueAndValidity();
      this.form.get('invoiceAddress')?.updateValueAndValidity();
    });

    this.form.get('purpose')?.valueChanges.subscribe((val: InformationRequestPurpose) => {
      // The institution name belongs to the selected option — switching options must not
      // carry the previously typed name over (LNA-1963).
      this.form.get('purposeInstitutionName')?.reset('');
      this.form.get('purposeInstitutionName')?.markAsUntouched();
      if (val === 'SubmissionToInstitution' || val === 'SubmissionToForeignInstitution') {
        this.form.get('purposeInstitutionName')?.setValidators([Validators.maxLength(MAX_LENGTH_NAME)]);
      } else {
        this.form.get('purposeInstitutionName')?.clearValidators();
      }
      if (val !== 'SubmissionToInstitution') {
        this.form.get('vsaaFlag')?.reset(false);
      }
      this.form.get('purposeInstitutionName')?.updateValueAndValidity();
    });

    this.form.get('serviceDeadline')?.valueChanges.subscribe((val: ServiceDeadlineType) => {
      const dateControl = this.form.get('serviceDeadlineDate');
      if (val === 'Other') {
        dateControl?.setValidators(Validators.required);
      } else {
        dateControl?.clearValidators();
        dateControl?.reset('');
        dateControl?.markAsUntouched();
      }
      dateControl?.updateValueAndValidity();
    });

    this.form.get('responseDeliveryMethod')?.valueChanges.subscribe((val: ResponseDeliveryMethod) => {
      this.form.get('responseEmail')?.clearValidators();
      this.form.get('responseEmail')?.reset('');
      this.form.get('responseEmail')?.markAsUntouched();
      this.form.get('responseAddress')?.clearValidators();
      this.form.get('responseAddress')?.reset('');
      this.form.get('responseAddress')?.markAsUntouched();
      this.form.get('responseEAddress')?.clearValidators();
      this.form.get('responseEAddress')?.reset('');
      this.form.get('responseEAddress')?.markAsUntouched();
      if (val === 'Email') {
        this.form.get('responseEmail')?.setValidators([emailValidator(), Validators.maxLength(MAX_LENGTH_EMAIL)]);
      } else if (val === 'PostalMail') {
        this.form.get('responseAddress')?.setValidators([Validators.maxLength(MAX_LONG_TEXT_LENGTH)]);
      } else if (val === 'EAddress') {
        this.form.get('responseEAddress')?.setValidators([Validators.maxLength(MAX_LETTER_NUMBER_LENGTH)]);
      }
      const responseUnitControl = this.form.get('responseDeliveryStructuralUnitId');
      if (val === 'Archive') {
        responseUnitControl?.setValidators(Validators.required);
      } else {
        responseUnitControl?.clearValidators();
        responseUnitControl?.reset('');
      }
      responseUnitControl?.updateValueAndValidity();
      this.form.get('responseEmail')?.updateValueAndValidity();
      this.form.get('responseAddress')?.updateValueAndValidity();
      this.form.get('responseEAddress')?.updateValueAndValidity();
    });
  }

  private clearRequesterOtherPersonFields(): void {
    if (this.isPopulating) return;
    ['otherPersonName', 'otherPersonSurname', 'otherPersonCode', 'authorizationNumber', 'relationshipType', 'deathCertificateData', 'otherPersonDescription'].forEach(key => {
      const control = this.form.get(key);
      control?.clearValidators();
      control?.reset('');
      control?.markAsUntouched();
    });
  }

  private updateOtherPersonValidity(): void {
    ['otherPersonName', 'otherPersonSurname', 'otherPersonCode', 'authorizationNumber', 'relationshipType', 'deathCertificateData', 'otherPersonDescription'].forEach(key => {
      this.form.get(key)?.updateValueAndValidity();
    });
  }

  private clearDiscountFields(): void {
    if (this.isPopulating) return;
    ['discountName', 'discountSurname', 'discountDocumentNumber', 'discountDocumentExpiry', 'discountDocumentIssuer', 'discountReason'].forEach(key => {
      const control = this.form.get(key);
      control?.clearValidators();
      control?.reset('');
      control?.markAsUntouched();
    });
  }

  private updateDiscountValidity(): void {
    ['discountName', 'discountSurname', 'discountDocumentNumber', 'discountDocumentExpiry', 'discountDocumentIssuer', 'discountReason'].forEach(key => {
      this.form.get(key)?.updateValueAndValidity();
    });
  }

  private clearPayerFields(): void {
    if (this.isPopulating) return;
    ['payerName', 'payerSurname', 'payerPersonalCode', 'payerCompanyName', 'payerRegistrationNumber', 'payerCountry', 'payerVatNumber', 'payerLegalEntityName', 'payerLegalForm', 'payerFreeOfChargeReason'].forEach(key => {
      const control = this.form.get(key);
      control?.clearValidators();
      control?.reset('');
      control?.markAsUntouched();
    });
  }

  private updatePayerValidity(): void {
    ['payerName', 'payerSurname', 'payerPersonalCode', 'payerCompanyName', 'payerRegistrationNumber', 'payerCountry', 'payerVatNumber', 'payerLegalEntityName', 'payerLegalForm', 'payerFreeOfChargeReason'].forEach(key => {
      this.form.get(key)?.updateValueAndValidity();
    });
  }

  // LNA-1940: FreeOfCharge payer implies FreeOfCharge invoice delivery, and locks it — the
  // combination can't be split. Doesn't force the value while populating from saved data, so
  // legacy/inconsistent combinations still display as actually stored.
  private syncInvoiceDeliveryWithPayerType(payerType: PayerType | null): void {
    const invoiceControl = this.form.get('invoiceDeliveryMethod');
    if (!invoiceControl) return;

    if (payerType === 'FreeOfCharge') {
      if (!this.isPopulating) {
        invoiceControl.setValue('FreeOfCharge');
      }
      invoiceControl.disable({ emitEvent: false });
    } else {
      invoiceControl.enable({ emitEvent: false });
      if (!this.isPopulating && invoiceControl.value === 'FreeOfCharge') {
        invoiceControl.setValue(null);
      }
    }
  }

  get requesterRelationType(): RequesterRelationType | null {
    return this.form.get('requesterRelationType')?.value;
  }

  get requesterForOtherPersonType(): RequesterForOtherPersonType | null {
    return this.form.get('requesterForOtherPersonType')?.value;
  }

  get payerType(): PayerType | null {
    return this.form.get('payerType')?.value;
  }

  get payerFreeOfChargeReason(): string | null {
    return this.form.get('payerFreeOfChargeReason')?.value;
  }

  get invoiceDeliveryMethod(): InvoiceDeliveryMethod | null {
    return this.form.get('invoiceDeliveryMethod')?.value;
  }

  get purpose(): InformationRequestPurpose | null {
    return this.form.get('purpose')?.value;
  }

  get serviceDeadline(): ServiceDeadlineType | null {
    return this.form.get('serviceDeadline')?.value;
  }

  get responseDeliveryMethod(): ResponseDeliveryMethod | null {
    return this.form.get('responseDeliveryMethod')?.value;
  }

  get discountGroupType(): DiscountGroupType | 'None' | null {
    return this.form.get('discountGroupType')?.value;
  }

  get legalFormName(): string {
    const code = this.legalEntity?.type || this.form.get('legalForm')?.value;
    if (!code) return '';
    return this.legalEntityFormOptions.find(o => o.code === code)?.name || code;
  }

  get showVsaaCheckbox(): boolean {
    return this.purpose === 'SubmissionToInstitution';
  }

  get showDiscountSection(): boolean {
    if (!this.isOrganization) return true;
    return !!this.form.get('jpAuthorizationChecked')?.value;
  }

  onDiscountDateSelected(date: string): void {
    this.form.get('discountDocumentExpiry')?.markAsDirty();
    this.form.get('discountDocumentExpiry')?.markAsTouched();
    this.form.get('discountDocumentExpiry')?.setValue(date);
  }

  onServiceDeadlineDateSelected(date: string): void {
    this.form.get('serviceDeadlineDate')?.setValue(date);
    this.form.get('serviceDeadlineDate')?.markAsTouched();
    this.form.markAsDirty();
  }

  selectDiscountGroup(type: DiscountGroupType | 'None'): void {
    this.form.get('discountGroupType')?.setValue(type);
    this.form.markAsDirty();
  }

  private isValidEnumValue(value: any): boolean {
    return !!value && value !== 'Unknown';
  }

  private patchFileNotesFromUploads(application: InformationRequestCard): void {
    const uploads = [this.jpAuthorizationUpload, this.authorizationUpload, this.deathCertificateUpload, this.discountGroupUpload];
    for (const upload of uploads) {
      if (!upload) continue;
      for (const desc of upload.filesDescriptions) {
        const doc = application.documents?.find(d => d.uploadFileName === desc.fileName);
        if (doc?.fileNote) {
          doc.fileNote.note = desc.description;
        }
      }
    }
  }

  private patchClientInfoFromForm(application: InformationRequestCard): void {
    if (this.isOrganization && application.legalEntity) {
      application.legalEntity.type = this.form.get('legalForm')?.value || application.legalEntity.type;
      application.legalEntity.legalAddress = this.form.get('legalAddress')?.value || application.legalEntity.legalAddress;
      application.legalEntity.phone = this.form.get('companyPhone')?.value || application.legalEntity.phone;
      application.legalEntity.email = this.form.get('companyEmail')?.value || application.legalEntity.email;
    } else {
      if (application.client) {
        application.client.email = this.form.get('email')?.value || application.client.email;
        application.client.phone = this.form.get('phone')?.value || application.client.phone;
      }
      if (!application.clientInfo) {
        application.clientInfo = {} as any;
      }
      application.clientInfo!.email = this.form.get('email')?.value || application.clientInfo!.email;
      application.clientInfo!.phone = this.form.get('phone')?.value || application.clientInfo!.phone;
    }
    application.contactPersonName = this.form.get('contactPersonName')?.value || application.contactPersonName;
    application.contactPersonSurname = this.form.get('contactPersonSurname')?.value || application.contactPersonSurname;
    application.contactPersonEmail = this.form.get('contactPersonEmail')?.value || application.contactPersonEmail;
    application.contactPersonPhone = this.form.get('contactPersonPhone')?.value || application.contactPersonPhone;
  }

  private buildLegalEntityUpdateArgs(): UpdateLegalEntityRequest | null {
    if (!this.isOrganization || !this.legalEntity) return null;
    return {
      id: this.legalEntity.id,
      registrationNumber: this.legalEntity.registrationNumber,
      name: this.legalEntity.name,
      type: (this.form.get('legalForm')?.value || this.legalEntity.type) as any,
      legalAddress: this.form.get('legalAddress')?.value || null,
      phone: this.form.get('companyPhone')?.value || null,
      email: this.form.get('companyEmail')?.value || null,
      countryId: this.legalEntity.country?.id || null
    };
  }

  private emitJpCompanyData(): void {
    if (!this.isOrganization) return;
    this.jpCompanyDataChanged.emit({
      legalForm: this.form.get('legalForm')?.value || '',
      legalAddress: this.form.get('legalAddress')?.value || '',
      companyPhone: this.form.get('companyPhone')?.value || '',
      companyEmail: this.form.get('companyEmail')?.value || '',
      contactPersonName: this.form.get('contactPersonName')?.value || '',
      contactPersonSurname: this.form.get('contactPersonSurname')?.value || '',
      contactPersonEmail: this.form.get('contactPersonEmail')?.value || '',
      contactPersonPhone: this.form.get('contactPersonPhone')?.value || ''
    });
  }

  private prefillLegalForm(entity: LegalEntityCard): void {
    if (!entity.type) return;
    const control = this.form.get('legalForm');
    if (control?.dirty) return;
    // The company lookup answers after the form is on screen. `markAsPristine()` on the whole group
    // therefore disowned whatever the operator had typed while it was in flight, and leaving the
    // step discarded those edits without a prompt (LNA-1850). Reset only what was prefilled, and
    // leave the group alone when it was already dirty.
    const wasDirty = this.form.dirty || this.filesTouched;
    control?.setValue(entity.type);
    control?.markAsPristine();
    if (!wasDirty) {
      this.form.markAsPristine();
      this.formChanged.emit(false);
    }
  }

  private applyDefaults(): void {
    const setIfNull = (key: string, value: any) => {
      if (this.form.get(key)?.value === null || this.form.get(key)?.value === '') {
        this.form.get(key)?.setValue(value);
      }
    };

    if (this.isOrganization) {
      // JP defaults
      setIfNull('payerType', 'Requester');
      setIfNull('invoiceDeliveryMethod', 'Portal');
      setIfNull('responseDeliveryMethod', 'Portal');
    } else {
      // FP defaults
      setIfNull('requesterRelationType', 'ForSelf');
      setIfNull('discountGroupType', 'None');
      setIfNull('payerType', 'Requester');
      setIfNull('invoiceDeliveryMethod', 'Portal');
      setIfNull('purpose', 'PrivateUse');
      setIfNull('responseDeliveryMethod', 'Portal');
    }
  }

  private populateFromApplication(): void {
    if (!this.application) return;
    this.isPopulating = true;

    const app = this.application;


    if (this.isOrganization) {
      if (app.contactPersonName) this.form.get('contactPersonName')?.setValue(app.contactPersonName);
      if (app.contactPersonSurname) this.form.get('contactPersonSurname')?.setValue(app.contactPersonSurname);
      if (app.contactPersonEmail) this.form.get('contactPersonEmail')?.setValue(app.contactPersonEmail);
      if (app.contactPersonPhone) this.form.get('contactPersonPhone')?.setValue(app.contactPersonPhone);

      if (app.legalEntity) {
        if (app.legalEntity.legalAddress) this.form.get('legalAddress')?.setValue(app.legalEntity.legalAddress);
        if (app.legalEntity.type) {
          this.form.get('legalForm')?.setValue(app.legalEntity.type);
        }
        if (app.legalEntity.phone) this.form.get('companyPhone')?.setValue(app.legalEntity.phone);
        if (app.legalEntity.email) this.form.get('companyEmail')?.setValue(app.legalEntity.email);
      }

      if (this.hasMeaningfulAuthorizationData(app)) {
        this.form.get('jpAuthorizationChecked')?.setValue(true);
      }
    } else {
      const fpPhone = this.fpContactData?.phone || app.phone || this.client?.phone || app.client?.phone || app.clientInfo?.phone;
      if (fpPhone) this.form.get('phone')?.setValue(fpPhone);
      const fpEmail = this.fpContactData?.email || this.client?.email || app.client?.email || app.clientInfo?.email;
      if (fpEmail) {
        this.form.get('email')?.setValue(fpEmail);
      }
    }

    if (this.isValidEnumValue(app.requesterRelationType)) {
      this.form.get('requesterRelationType')?.setValue(app.requesterRelationType);
    } else if (this.isValidEnumValue(app.requesterForOtherPersonType)) {
      this.form.get('requesterRelationType')?.setValue('ForOtherPerson');
    }
    if (this.isValidEnumValue(app.requesterForOtherPersonType)) this.form.get('requesterForOtherPersonType')?.setValue(app.requesterForOtherPersonType);

    if (app.requesterForOtherPersonData) {
      try {
        const data = JSON.parse(app.requesterForOtherPersonData);
        if (data.name) this.form.get('otherPersonName')?.setValue(data.name);
        if (data.surname) this.form.get('otherPersonSurname')?.setValue(data.surname);
        if (data.personalCode) this.form.get('otherPersonCode')?.setValue(data.personalCode);
        if (data.authorizationNumber) this.form.get('authorizationNumber')?.setValue(data.authorizationNumber);
        if (data.relationshipType) this.form.get('relationshipType')?.setValue(data.relationshipType);
        if (data.deathCertificateData) this.form.get('deathCertificateData')?.setValue(data.deathCertificateData);
        if (data.description) this.form.get('otherPersonDescription')?.setValue(data.description);
      } catch { /* ignore parse errors */ }
    }

    if (this.isValidEnumValue(app.discountGroupType)) {
      this.form.get('discountGroupType')?.setValue(app.discountGroupType);
    } else if (app.discountGroupData) {
      try {
        const parsed = JSON.parse(app.discountGroupData);
        if (parsed.none) this.form.get('discountGroupType')?.setValue('None');
      } catch { /* ignore */ }
    }
    if (app.discountGroupData) {
      try {
        const data = JSON.parse(app.discountGroupData);
        if (data.name) this.form.get('discountName')?.setValue(data.name);
        if (data.surname) this.form.get('discountSurname')?.setValue(data.surname);
        if (data.documentNumber) this.form.get('discountDocumentNumber')?.setValue(data.documentNumber);
        if (data.documentExpiry) this.form.get('discountDocumentExpiry')?.setValue(data.documentExpiry);
        if (data.documentIssuer) this.form.get('discountDocumentIssuer')?.setValue(data.documentIssuer);
        if (data.reason) this.form.get('discountReason')?.setValue(data.reason);
      } catch { /* ignore parse errors */ }
    }

    if (this.isValidEnumValue(app.payerType)) this.form.get('payerType')?.setValue(app.payerType);
    if (app.payerFreeOfChargeReason) this.form.get('payerFreeOfChargeReason')?.setValue(app.payerFreeOfChargeReason);
    if (app.payerData) {
      try {
        const data = JSON.parse(app.payerData);
        if (data.name) this.form.get('payerName')?.setValue(data.name);
        if (data.surname) this.form.get('payerSurname')?.setValue(data.surname);
        if (data.personalCode) this.form.get('payerPersonalCode')?.setValue(data.personalCode);
        if (data.companyName) this.form.get('payerLegalEntityName')?.setValue(data.companyName);
        if (data.registrationNumber) this.form.get('payerRegistrationNumber')?.setValue(data.registrationNumber);
        if (data.country) this.form.get('payerCountry')?.setValue(data.country);
        if (data.vatNumber) this.form.get('payerVatNumber')?.setValue(data.vatNumber);
        if (data.legalForm) this.form.get('payerLegalForm')?.setValue(data.legalForm);
      } catch { /* ignore parse errors */ }
    }

    if (this.isValidEnumValue(app.invoiceDeliveryMethod)) this.form.get('invoiceDeliveryMethod')?.setValue(app.invoiceDeliveryMethod);
    if (app.invoiceDeliveryDetail && app.invoiceDeliveryMethod === 'Email') {
      this.form.get('invoiceEmail')?.setValue(app.invoiceDeliveryDetail);
    } else if (app.invoiceDeliveryDetail && app.invoiceDeliveryMethod === 'EAddress') {
      this.form.get('invoiceEAddress')?.setValue(app.invoiceDeliveryDetail);
    } else if (app.invoiceDeliveryDetail && app.invoiceDeliveryMethod === 'PostalMail') {
      this.form.get('invoiceAddress')?.setValue(app.invoiceDeliveryDetail);
    }
    if (app.invoiceDeliveryStructuralUnitId) {
      this.form.get('invoiceDeliveryStructuralUnitId')?.setValue(app.invoiceDeliveryStructuralUnitId);
    }
    if (this.isValidEnumValue(app.purpose)) this.form.get('purpose')?.setValue(app.purpose);
    if (app.purposeInstitutionName) this.form.get('purposeInstitutionName')?.setValue(app.purposeInstitutionName);
    if (app.vsaaFlag) this.form.get('vsaaFlag')?.setValue(app.vsaaFlag);
    if (this.isValidEnumValue(app.serviceDeadline)) this.form.get('serviceDeadline')?.setValue(app.serviceDeadline);
    if (app.serviceDeadline === 'Other' && app.deadlineDate) {
      this.form.get('serviceDeadlineDate')?.setValue(app.deadlineDate.split('T')[0]);
    }
    if (this.isValidEnumValue(app.responseDeliveryMethod)) this.form.get('responseDeliveryMethod')?.setValue(app.responseDeliveryMethod);
    if (app.responseDeliveryDetail && app.responseDeliveryMethod === 'Email') {
      this.form.get('responseEmail')?.setValue(app.responseDeliveryDetail);
    } else if (app.responseDeliveryDetail && app.responseDeliveryMethod === 'PostalMail') {
      this.form.get('responseAddress')?.setValue(app.responseDeliveryDetail);
    } else if (app.responseDeliveryDetail && app.responseDeliveryMethod === 'EAddress') {
      this.form.get('responseEAddress')?.setValue(app.responseDeliveryDetail);
    }
    if (app.responseDeliveryStructuralUnitId) {
      this.form.get('responseDeliveryStructuralUnitId')?.setValue(app.responseDeliveryStructuralUnitId);
    }
    if (app.notes) this.form.get('notes')?.setValue(app.notes);

    // BO-specific fields
    if (this.isValidEnumValue(app.submissionChannel)) this.form.get('submissionChannel')?.setValue(app.submissionChannel);
    if (app.letterNumber) this.form.get('letterNumber')?.setValue(app.letterNumber);
    if (app.aboutSubject) this.form.get('aboutSubject')?.setValue(app.aboutSubject);
    if (app.requiresRestrictedAccess) this.form.get('requiresRestrictedAccess')?.setValue(app.requiresRestrictedAccess);
    this.initialDiscountGroupType = this.form.get('discountGroupType')?.value || null;
  }

  private collectRequesterForOtherPersonData(): string | null {
    if (this.isOrganization && this.form.get('jpAuthorizationChecked')?.value) {
      return JSON.stringify({
        name: this.form.get('otherPersonName')?.value,
        surname: this.form.get('otherPersonSurname')?.value,
        personalCode: this.form.get('otherPersonCode')?.value,
        authorizationNumber: this.form.get('authorizationNumber')?.value
      });
    }

    if (this.requesterRelationType === 'OtherCase') {
      return JSON.stringify({
        description: this.form.get('otherPersonDescription')?.value
      });
    }

    const type = this.requesterForOtherPersonType;
    if (!type) return null;

    if (type === 'AuthorizationOrPermission') {
      return JSON.stringify({
        name: this.form.get('otherPersonName')?.value,
        surname: this.form.get('otherPersonSurname')?.value,
        personalCode: this.form.get('otherPersonCode')?.value,
        authorizationNumber: this.form.get('authorizationNumber')?.value
      });
    } else if (type === 'DeceasedRelative') {
      return JSON.stringify({
        relationshipType: this.form.get('relationshipType')?.value,
        name: this.form.get('otherPersonName')?.value,
        surname: this.form.get('otherPersonSurname')?.value,
        personalCode: this.form.get('otherPersonCode')?.value,
        deathCertificateData: this.form.get('deathCertificateData')?.value
      });
    } else if (type === 'Other') {
      return JSON.stringify({
        description: this.form.get('otherPersonDescription')?.value
      });
    }

    return null;
  }

  private collectDiscountGroupData(): string | null {
    if (!this.discountGroupType) return null;
    if (this.discountGroupType === 'None') return JSON.stringify({ none: true });

    if (this.discountGroupType === 'ChildProtection' || this.discountGroupType === 'OtherCase') {
      return JSON.stringify({
        reason: this.form.get('discountReason')?.value
      });
    }

    const data: Record<string, string> = {
      name: this.form.get('discountName')?.value,
      surname: this.form.get('discountSurname')?.value,
      documentNumber: this.form.get('discountDocumentNumber')?.value
    };

    if (this.discountGroupType === 'FirstOrSecondGroupDisability' || this.discountGroupType === 'LowIncomePerson') {
      data['documentExpiry'] = this.form.get('discountDocumentExpiry')?.value;
    }
    if (this.discountGroupType === 'LowIncomePerson') {
      data['documentIssuer'] = this.form.get('discountDocumentIssuer')?.value;
    }

    return JSON.stringify(data);
  }

  private collectPayerData(): string | null {
    const type = this.payerType;
    if (!type) return null;

    if (type === 'Requester') {
      const vatNumber = this.form.get('payerVatNumber')?.value;
      if (this.isOrganization && vatNumber) {
        return JSON.stringify({ payerType: type, vatNumber });
      }
      return null;
    }

    if (type === 'OtherPhysicalPerson') {
      return JSON.stringify({
        payerType: type,
        name: this.form.get('payerName')?.value,
        surname: this.form.get('payerSurname')?.value,
        personalCode: this.form.get('payerPersonalCode')?.value
      });
    } else if (type === 'OtherLegalEntity') {
      return JSON.stringify({
        payerType: type,
        country: this.form.get('payerCountry')?.value,
        registrationNumber: this.form.get('payerRegistrationNumber')?.value,
        vatNumber: this.form.get('payerVatNumber')?.value,
        companyName: this.form.get('payerLegalEntityName')?.value,
        legalForm: this.form.get('payerLegalForm')?.value
      });
    }

    return null;
  }

  private buildUpdateArgs(): UpdateBoInformationRequestRequest {
    return {
      topicType: this.application!.topicType,
      topicVersionId: this.application!.topicVersionId,
      topicFieldData: this.application!.topicFieldData,
      blockCount: this.application!.blockCount,
      personalCode: this.isPersonalCodeEditable ? (this.form.get('personalCode')?.value || null) : undefined,
      phone: this.form.get('phone')?.value || this.form.get('contactPersonPhone')?.value || null,
      email: this.form.get('email')?.value || this.form.get('contactPersonEmail')?.value || null,
      requesterRelationType: this.requesterRelationType || undefined,
      requesterForOtherPersonType: this.isOrganization
        ? (this.form.get('jpAuthorizationChecked')?.value ? 'AuthorizationOrPermission' as RequesterForOtherPersonType : null as any)
        : (this.requesterRelationType === 'ForOtherPerson' ? this.requesterForOtherPersonType || null as any : null as any),
      requesterForOtherPersonData: (this.requesterRelationType === 'ForOtherPerson' || this.requesterRelationType === 'OtherCase' || (this.isOrganization && this.form.get('jpAuthorizationChecked')?.value))
        ? this.collectRequesterForOtherPersonData() : '',
      discountGroupType: this.discountGroupType ? this.discountGroupType as DiscountGroupType : null as any,
      discountGroupData: this.collectDiscountGroupData(),
      payerType: this.payerType || undefined,
      payerData: this.collectPayerData(),
      payerFreeOfChargeReason: this.payerType === 'FreeOfCharge' ? (this.payerFreeOfChargeReason || null) : null,
      invoiceDeliveryMethod: this.invoiceDeliveryMethod || undefined,
      invoiceDeliveryDetail: this.invoiceDeliveryMethod === 'Email'
        ? this.form.get('invoiceEmail')?.value
        : this.invoiceDeliveryMethod === 'EAddress'
          ? this.form.get('invoiceEAddress')?.value
          : this.invoiceDeliveryMethod === 'PostalMail'
            ? this.form.get('invoiceAddress')?.value
            : null,
      invoiceDeliveryStructuralUnitId: this.invoiceDeliveryMethod === 'Archive'
        ? (this.form.get('invoiceDeliveryStructuralUnitId')?.value || null)
        : null,
      purpose: this.purpose || undefined,
      purposeInstitutionName: (this.purpose === 'SubmissionToInstitution' || this.purpose === 'SubmissionToForeignInstitution') ? this.form.get('purposeInstitutionName')?.value : null,
      vsaaFlag: this.form.get('vsaaFlag')?.value || false,
      serviceDeadline: this.serviceDeadline || undefined,
      serviceDeadlineDate: this.serviceDeadline === 'Other' ? (this.form.get('serviceDeadlineDate')?.value || null) : null,
      responseDeliveryMethod: this.responseDeliveryMethod || undefined,
      responseDeliveryDetail: this.responseDeliveryMethod === 'Email'
        ? this.form.get('responseEmail')?.value
        : this.responseDeliveryMethod === 'PostalMail'
          ? this.form.get('responseAddress')?.value
          : this.responseDeliveryMethod === 'EAddress'
            ? this.form.get('responseEAddress')?.value
            : null,
      responseDeliveryStructuralUnitId: this.responseDeliveryMethod === 'Archive'
        ? (this.form.get('responseDeliveryStructuralUnitId')?.value || null)
        : null,
      notes: this.form.get('notes')?.value || null,
      submissionChannel: this.form.get('submissionChannel')?.value || undefined,
      letterNumber: this.form.get('letterNumber')?.value || null,
      aboutSubject: this.form.get('aboutSubject')?.value || null,
      requiresRestrictedAccess: this.form.get('requiresRestrictedAccess')?.value || false,
      contactPersonName: this.isOrganization ? (this.form.get('contactPersonName')?.value || null) : null,
      contactPersonSurname: this.isOrganization ? (this.form.get('contactPersonSurname')?.value || null) : null,
      contactPersonEmail: this.isOrganization ? (this.form.get('contactPersonEmail')?.value || null) : null,
      contactPersonPhone: this.isOrganization ? (this.form.get('contactPersonPhone')?.value || null) : null,
      legalEntityType: this.isOrganization ? (this.form.get('legalForm')?.value || null) : null,
      legalEntityLegalAddress: this.isOrganization ? (this.form.get('legalAddress')?.value || null) : null,
      legalEntityEmail: this.isOrganization ? (this.form.get('companyEmail')?.value || null) : null,
      legalEntityPhone: this.isOrganization ? (this.form.get('companyPhone')?.value || null) : null,
      mustCompleteByDate: this.application?.mustCompleteByDate || undefined
    };
  }

  override submit(): void {
    this.submitAttempted = true;
    this.validationTriggered = true;
    this.form.markAllAsTouched();

    if (!this.form.valid) {
      this.toastService.showDanger('formHasErrors', '', { shouldTranslate: true });
      return;
    }

    if (!this.application) {
      this.toastService.showDanger('formHasErrors', '', { shouldTranslate: true });
      return;
    }

    this.isLoading = true;
    this.emitJpCompanyData();

    const updateRequest$ = this.apiService.updateInformationRequest(this.application.id, this.buildUpdateArgs());
    const legalEntityArgs = this.buildLegalEntityUpdateArgs();
    const requests$: any[] = [updateRequest$];
    if (legalEntityArgs) {
      requests$.push(this.apiService.updateLegalEntity(legalEntityArgs));
    }

    // Delete server files that were cleared via radio switches
    const deletedIds = new Set(this.pendingServerFileDeletions);
    const clearedTypes = this.clearedTypesExcludingUploads();
    this.pendingServerFileDeletions.forEach(id => {
      requests$.push(this.apiService.deleteFile(id));
    });
    this.pendingServerFileDeletions = [];
    this.clearedFileTypes.clear();
    this.filesTouched = false;

    forkJoin(requests$).subscribe({
      next: (results: any[]) => {
        const data = results[0];
        const legalEntityData = legalEntityArgs ? results[1] : null;
        // Strip files of cleared types (handles stale server docs uploaded via draftSaved)
        if (data.documents && (clearedTypes.size > 0 || deletedIds.size > 0)) {
          const docsToRemove = data.documents.filter((d: any) =>
            clearedTypes.has(d.objectType) || deletedIds.has(d.outerId)
          );
          // Fire-and-forget deletion for server files we didn't know about locally
          docsToRemove.forEach((d: any) => {
            if (d.outerId && !deletedIds.has(d.outerId)) {
              this.apiService.deleteFile(d.outerId).subscribe();
            }
          });
          data.documents = data.documents.filter((d: any) =>
            !clearedTypes.has(d.objectType) && !deletedIds.has(d.outerId)
          );
        }
        if (this.discountGroupType) {
          data.discountGroupType = this.discountGroupType as any;
        }
        this.application = data;
        this.patchClientInfoFromForm(data);
        this.patchFileNotesFromUploads(data);
        this.applicationUpdated.emit(data);
        if (legalEntityData) this.legalEntityUpdated.emit(legalEntityData);
        this.draftSaved.emit();
        this.isLoading = false;
        this.router.navigate([], {
          queryParams: { solis: 3 },
          queryParamsHandling: 'merge'
        });
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.showRequestError(err);
      }
    });
  }

  saveInlineEdit(): void {
    this.submitAttempted = true;
    this.validationTriggered = true;
    this.form.markAllAsTouched();

    if (!this.form.valid) {
      this.toastService.showDanger('formHasErrors', '', { shouldTranslate: true });
      return;
    }

    if (!this.application) return;

    this.isLoading = true;
    this.emitJpCompanyData();

    const args = this.buildUpdateArgs();
    // In inline edit mode, preserve BO fields from application (managed by Pamatinformācija section)
    args.submissionChannel = this.application!.submissionChannel || undefined;
    args.letterNumber = this.application!.letterNumber || null;
    args.aboutSubject = this.application!.aboutSubject || null;
    args.requiresRestrictedAccess = this.application!.requiresRestrictedAccess || false;
    args.serviceDeadline = this.application!.serviceDeadline || undefined;
    args.serviceDeadlineDate = this.application!.serviceDeadlineDate || (this.application as any).deadlineDate || null;
    args.mustCompleteByDate = this.application!.mustCompleteByDate || null;
    const updateRequest$ = this.apiService.updateInformationRequest(this.application.id, args);
    const legalEntityArgs = this.buildLegalEntityUpdateArgs();
    const requests$: any[] = [updateRequest$];
    const skipLegalEntitySync = this.application.backofficeStatus === 'Completed' || this.application.backofficeStatus === 'Cancelled';
    if (legalEntityArgs && !skipLegalEntitySync) {
      requests$.push(this.apiService.updateLegalEntity(legalEntityArgs));
    }

    // Delete documents that are no longer relevant after field changes
    const fileDeletions$: Observable<any>[] = [];
    const deletedFileIds = new Set<string>();

    // Include pending deletions from radio switches
    this.pendingServerFileDeletions.forEach(id => {
      if (!deletedFileIds.has(id)) {
        deletedFileIds.add(id);
        fileDeletions$.push(this.apiService.deleteFile(id));
      }
    });
    this.pendingServerFileDeletions = [];
    const inlineClearedTypes = this.clearedTypesExcludingUploads();
    this.clearedFileTypes.clear();
    this.filesTouched = false;

    const needsAuthOrDeathCertDocs = this.isOrganization
      ? !!this.form.get('jpAuthorizationChecked')?.value
      : this.requesterRelationType === 'ForOtherPerson';
    const needsDiscountDocs = this.discountGroupType && this.discountGroupType !== 'None';

    if (!needsAuthOrDeathCertDocs) {
      (this.application?.documents || [])
        .filter(d => d.objectType === this.fileTypeAuthorization || (d.objectType as string) === this.fileTypeDeathCertificate)
        .forEach(f => {
          if (f.outerId && !deletedFileIds.has(f.outerId)) {
            deletedFileIds.add(f.outerId);
            fileDeletions$.push(this.apiService.deleteFile(f.outerId));
          }
        });
    }
    if (!needsDiscountDocs || this.discountTypeWasChanged) {
      (this.application?.documents || []).filter(d => d.objectType === this.fileTypeDiscount).forEach(f => {
        if (f.outerId && !deletedFileIds.has(f.outerId)) {
          deletedFileIds.add(f.outerId);
          fileDeletions$.push(this.apiService.deleteFile(f.outerId));
        }
      });
    }
    requests$.push(...fileDeletions$);

    // Upload new files and handle file description updates/deletions
    const fileObservables = this.collectFileObservables();
    requests$.push(...fileObservables);

    forkJoin(requests$).subscribe({
      next: (results: any[]) => {
        const data = results[0];
        const legalEntityData = (legalEntityArgs && !skipLegalEntitySync) ? results[1] : null;
        // Strip files of cleared types from response
        if (data.documents && inlineClearedTypes.size > 0) {
          const docsToRemove = data.documents.filter((d: any) => inlineClearedTypes.has(d.objectType));
          docsToRemove.forEach((d: any) => {
            if (d.outerId && !deletedFileIds.has(d.outerId)) {
              this.apiService.deleteFile(d.outerId).subscribe();
            }
          });
          data.documents = data.documents.filter((d: any) => !inlineClearedTypes.has(d.objectType));
        }
        this.application = data;
        this.patchClientInfoFromForm(data);
        this.patchFileNotesFromUploads(data);
        this.applicationUpdated.emit(data);
        if (legalEntityData) this.legalEntityUpdated.emit(legalEntityData);
        this.isLoading = false;
        this.toastService.showSuccess('infoRequestSectionUpdated', '', { shouldTranslate: true });
        this.inlineEditSaved.emit(data);
      },
      error: (err) => {
        this.isLoading = false;
        if (this.isDuplicateFileError(err)) {
          this.toastService.showDanger('duplicateFileError', '', { shouldTranslate: true });
        } else {
          this.toastService.showRequestError(err);
        }
      }
    });
  }

  private hasMeaningfulAuthorizationData(app: InformationRequestCard): boolean {
    if (app.requesterForOtherPersonType === 'AuthorizationOrPermission') return true;
    if ((app.documents || []).some(d => d.objectType === this.fileTypeAuthorization)) return true;
    if (!app.requesterForOtherPersonData) return false;
    try {
      const data = JSON.parse(app.requesterForOtherPersonData);
      return !!(data.name || data.surname || data.personalCode || data.authorizationNumber);
    } catch {
      return false;
    }
  }

  private isDuplicateFileError(err: any): boolean {
    const errors = err?.error?.errors;
    if (!errors) return false;
    return Object.values(errors).some(
      (msgs: any) => Array.isArray(msgs) && msgs.some((m: string) => m === 'Such file already exists.')
    );
  }

  handleSaveDraft(): void {
    if (!this.application) {
      this.toastService.showDanger('formHasErrors', '', { shouldTranslate: true });
      return;
    }

    this.isLoading = true;

    const updateRequest$ = this.apiService.updateInformationRequest(this.application.id, this.buildUpdateArgs());
    const legalEntityArgs = this.buildLegalEntityUpdateArgs();
    const requests$: any[] = [updateRequest$];
    if (legalEntityArgs) {
      requests$.push(this.apiService.updateLegalEntity(legalEntityArgs));
    }

    // Delete server files that were cleared via radio switches
    const deletedIds = new Set(this.pendingServerFileDeletions);
    const clearedTypes = this.clearedTypesExcludingUploads();
    this.pendingServerFileDeletions.forEach(id => {
      requests$.push(this.apiService.deleteFile(id));
    });
    this.pendingServerFileDeletions = [];
    this.clearedFileTypes.clear();
    this.filesTouched = false;

    forkJoin(requests$).subscribe({
      next: (results: any[]) => {
        const data = results[0];
        const legalEntityData = legalEntityArgs ? results[1] : null;
        if (data.documents && (clearedTypes.size > 0 || deletedIds.size > 0)) {
          const docsToRemove = data.documents.filter((d: any) =>
            clearedTypes.has(d.objectType) || deletedIds.has(d.outerId)
          );
          docsToRemove.forEach((d: any) => {
            if (d.outerId && !deletedIds.has(d.outerId)) {
              this.apiService.deleteFile(d.outerId).subscribe();
            }
          });
          data.documents = data.documents.filter((d: any) =>
            !clearedTypes.has(d.objectType) && !deletedIds.has(d.outerId)
          );
        }
        this.toastService.showSuccess('draftSaved', '', { shouldTranslate: true });
        this.application = data;
        this.patchClientInfoFromForm(data);
        this.patchFileNotesFromUploads(data);
        this.applicationUpdated.emit(data);
        if (legalEntityData) this.legalEntityUpdated.emit(legalEntityData);
        this.isLoading = false;
        this.form.markAsPristine();
        this.formChanged.emit(false);
        this.draftSaved.emit();
      },
      error: (err) => {
        this.isLoading = false;
        this.toastService.showRequestError(err);
      }
    });
  }

  override back(confirmed: boolean = false): void {
    if (!confirmed && (this.form.dirty || this.filesTouched)) {
      this.isOpenConfirmBack = true;
    } else {
      this.router.navigate([], {
        queryParams: { solis: 1 },
        queryParamsHandling: 'merge'
      });
    }
  }

  isConfirmDelete: boolean = false;

  onDelete(): void {
    this.isConfirmDelete = true;
  }

  confirmDelete(): void {
    this.isConfirmDelete = false;
    if (this.application?.id) {
      this.apiService.deleteInformationRequest(this.application.id).subscribe({
        next: () => {
          this.toastService.showSuccess('applicationDraftDeleted', '', { shouldTranslate: true });
          this.router.navigate(['/informacijas-pieprasijums']);
        },
        error: () => {
          this.toastService.showRequestError();
        }
      });
    } else {
      this.router.navigate(['/informacijas-pieprasijums']);
    }
  }
}
